// MARK://dhk8/pos/features/pos/PickupDeliveryPanel
// "Entregar pedido" — validación de entrega por código de recolección.
//
// El problema que resuelve: cuando el comensal llega por su pedido para llevar,
// el staff no tenía forma de saber CUÁL de los pedidos en el mostrador es suyo.
// El "folio" que la app del comensal enseñaba se calculaba en el cliente
// (orderId.slice(0,8)) y no correspondía a nada consultable.
//
// Ahora el servidor genera un código dictable (XXXX-99999) al crear la orden. El
// staff lo teclea aquí, ve DE QUIÉN es el pedido y qué lleva, y sólo entonces
// pulsa "Validar y entregar". El backend rechaza la doble entrega.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PackageCheck, Search, AlertTriangle, Check, CreditCard } from 'lucide-react'

import { lookupOrderByPickupCode, deliverOrder, type PickupLookupResult } from '../../lib/clients/orders.js'
import { getTenantId } from '../../lib/api.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { formatCents } from '../../lib/money.js'

/** Ubicación de entrega que el comensal capturó en el modo estadio del guest
 *  (Sección/Fila/Asiento). Viaja en la nota de cada ítem con el prefijo
 *  "Entregar en …" (así también sale en Entrantes, KDS y comanda sin columnas
 *  nuevas). Aquí la sacamos UNA vez para que quien entrega la vea en grande. */
const DELIVER_TO_RE = /Entregar en ([^|]+)/
function deliverToFrom(items: Array<{ notes?: string | null }>): string | null {
  for (const it of items) {
    const m = it.notes ? DELIVER_TO_RE.exec(it.notes) : null
    if (m?.[1]) return m[1].trim()
  }
  return null
}
/** Nota del ítem SIN la ubicación (ya se muestra arriba). */
function itemNoteWithoutLocation(notes?: string | null): string {
  return (notes ?? '').replace(DELIVER_TO_RE, '').replace(/\s*\|\s*$/, '').replace(/^\s*\|\s*/, '').trim()
}

export function PickupDeliveryPanel({ onDelivered }: { onDelivered?: () => void }) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [code, setCode] = useState('')
  const [found, setFound] = useState<PickupLookupResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [delivered, setDelivered] = useState<string | null>(null)

  async function search(): Promise<void> {
    const tid = getTenantId()
    if (!tid) { setError('No hay tenant activo.'); return }
    setBusy(true); setError(null); setFound(null); setDelivered(null)
    try {
      setFound(await lookupOrderByPickupCode(tid, code.trim()))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No encontramos ningún pedido con ese código')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(): Promise<void> {
    const tid = getTenantId()
    if (!tid || !found) return
    setBusy(true); setError(null)
    try {
      await deliverOrder(found.id, tid, user?.id ?? '00000000-0000-0000-0000-000000000001')
      setDelivered(found.customerName ?? found.pickupCode ?? found.folio)
      setFound(null)
      setCode('')
      onDelivered?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo marcar la entrega')
    } finally {
      setBusy(false)
    }
  }

  const liveItems = (found?.items ?? []).filter((it) => it.status !== 'CANCELLED')
  const deliverTo = deliverToFrom(liveItems)
  const paid = !!found && found.totalCents > 0 && (found.paidCents ?? 0) >= found.totalCents
  // Saldo pendiente → hay que COBRAR antes de entregar (flujo "aceptar sin pago":
  // la orden salió a cocina impaga y se cobra al recoger). "Cobrar para entregar"
  // carga la orden en la Barra (/order?pay=1) con el método preseleccionado.
  const needsCharge = !!found && found.totalCents > (found.paidCents ?? 0)
  function goCharge() {
    if (!found) return
    navigate(`/order?orderId=${encodeURIComponent(found.id)}&pay=1`)
  }

  return (
    <section className="dhk8-pkp">
      <div className="dhk8-pkp__head">
        <PackageCheck size={18} aria-hidden />
        <div>
          <h2 className="dhk8-pkp__title">Entregar pedido</h2>
          <p className="dhk8-pkp__hint">
            Pide al cliente su código de recolección y tecléalo para confirmar de quién es.
          </p>
        </div>
      </div>

      <div className="dhk8-pkp__search">
        <input
          className="dhk8-pkp__input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') void search() }}
          placeholder="XXXX-99999"
          aria-label="Código de recolección"
          autoComplete="off"
          spellCheck={false}
        />
        <button type="button" className="dhk8-pkp__btn" disabled={busy || code.trim().length < 9} onClick={() => void search()}>
          <Search size={15} /> Buscar
        </button>
      </div>

      {error && (
        <div className="dhk8-pkp__err" role="alert">
          <AlertTriangle size={15} aria-hidden /> {error}
        </div>
      )}

      {delivered && (
        <div className="dhk8-pkp__ok" role="status">
          <Check size={15} aria-hidden /> Pedido entregado a {delivered}.
        </div>
      )}

      {found && (
        <div className="dhk8-pkp__card">
          <div className="dhk8-pkp__who">
            <span className="dhk8-pkp__who-label">Este pedido es de</span>
            <strong className="dhk8-pkp__who-name">{found.customerName ?? 'Cliente sin nombre registrado'}</strong>
            <span className="dhk8-pkp__code">{found.pickupCode}</span>
            {deliverTo && (
              <span className="dhk8-pkp__where">Entregar en {deliverTo}</span>
            )}
          </div>

          <ul className="dhk8-pkp__items">
            {liveItems.map((it) => (
              <li key={it.id} className="dhk8-pkp__item">
                <span className="dhk8-pkp__qty">{it.quantity}×</span>
                <span className="dhk8-pkp__name">
                  {it.name}
                  {itemNoteWithoutLocation(it.notes) && (
                    <span className="dhk8-pkp__note"> · {itemNoteWithoutLocation(it.notes)}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="dhk8-pkp__foot">
            <div className="dhk8-pkp__totals">
              <strong>{formatCents(found.totalCents)}</strong>
              {/* El staff DEBE ver si el pedido trae saldo: entregar comida sin
                  cobrar es una fuga de dinero, no un detalle de UI. */}
              <span className={paid ? 'dhk8-pkp__paid' : 'dhk8-pkp__due'}>
                {paid ? 'Pagado' : `Por cobrar ${formatCents(found.totalCents - (found.paidCents ?? 0))}`}
              </span>
            </div>
            {needsCharge ? (
              <button type="button" className="dhk8-pkp__confirm" disabled={busy} onClick={goCharge}
                title="Cobrar el pedido en la Barra y luego entregarlo">
                <CreditCard size={16} /> Cobrar para entregar
              </button>
            ) : (
              <button type="button" className="dhk8-pkp__confirm" disabled={busy} onClick={() => void confirm()}>
                <Check size={16} /> Validar y entregar
              </button>
            )}
          </div>
        </div>
      )}

      <style>{styles}</style>
    </section>
  )
}

const styles = `
  .dhk8-pkp {
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: var(--pos-space-4, 16px);
  }
  .dhk8-pkp__head { display: flex; align-items: flex-start; gap: 10px; color: var(--pos-tint-on, #48C76C); }
  .dhk8-pkp__title {
    margin: 0; font-size: var(--pos-text-lg, 17px); font-weight: var(--pos-weight-semibold, 600);
    color: var(--pos-fg, #E8EFE9); font-family: var(--font-display, system-ui);
  }
  .dhk8-pkp__hint { margin: 2px 0 0; font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-pkp__search { display: flex; gap: 8px; }
  .dhk8-pkp__input {
    flex: 1; min-width: 0;
    background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: 12px 14px; font: inherit;
    font-family: var(--font-mono, ui-monospace);
    font-size: var(--pos-text-xl, 20px); letter-spacing: .12em;
    min-height: var(--pos-touch-target, 44px);
  }
  .dhk8-pkp__input:focus { outline: none; border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-pkp__btn, .dhk8-pkp__confirm {
    background: var(--pos-tint, #1F8B3A); color: #fff; border: 1px solid var(--pos-tint, #1F8B3A);
    padding: 10px 16px; font: inherit; font-size: var(--pos-text-sm, 13px);
    font-weight: var(--pos-weight-semibold, 600); cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    min-height: var(--pos-touch-target, 44px); flex-shrink: 0;
  }
  .dhk8-pkp__btn:disabled, .dhk8-pkp__confirm:disabled { opacity: .5; cursor: not-allowed; }
  .dhk8-pkp__btn:hover:not(:disabled), .dhk8-pkp__confirm:hover:not(:disabled) { filter: brightness(1.08); }

  .dhk8-pkp__err, .dhk8-pkp__ok {
    display: flex; align-items: center; gap: 6px;
    font-size: var(--pos-text-sm, 13px); padding: 10px 12px;
  }
  .dhk8-pkp__err {
    color: var(--pos-danger, #E5484D);
    border: 1px solid color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent);
    background: var(--pos-danger-bg, rgba(229,72,77,.10));
  }
  .dhk8-pkp__ok {
    color: var(--pos-tint-on, #48C76C);
    border: 1px solid color-mix(in oklch, var(--pos-tint, #1F8B3A) 35%, transparent);
    background: var(--pos-tint-soft, rgba(31,139,58,.14));
  }

  .dhk8-pkp__card {
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    background: var(--pos-elevated, #1E2520);
    padding: var(--pos-space-4, 16px);
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
  }
  .dhk8-pkp__who { display: flex; flex-direction: column; gap: 2px; }
  .dhk8-pkp__who-label {
    font-size: var(--pos-text-xxs, 11px); text-transform: uppercase; letter-spacing: .06em;
    color: var(--pos-fg-tertiary, #7E8C82); font-weight: var(--pos-weight-semibold, 600);
  }
  .dhk8-pkp__who-name {
    font-size: var(--pos-text-2xl, 24px); color: var(--pos-fg, #E8EFE9);
    font-family: var(--font-display, system-ui); font-weight: 700; line-height: 1.2;
  }
  .dhk8-pkp__code {
    font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-sm, 13px);
    color: var(--pos-fg-secondary, #B6BFB8); letter-spacing: .1em; margin-top: 2px;
  }

  .dhk8-pkp__where {
    margin-top: 6px; align-self: flex-start;
    font-size: var(--pos-text-md, 15px); font-weight: 700; color: var(--pos-fg, #E8EFE9);
    background: var(--pos-tint-soft, rgba(31,139,58,.14));
    border: 1px solid color-mix(in oklch, var(--pos-tint, #1F8B3A) 45%, transparent);
    padding: 6px 10px;
  }
  .dhk8-pkp__note { color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-sm, 13px); }
  .dhk8-pkp__items { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px;
    border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10)); padding-top: var(--pos-space-3, 12px); }
  .dhk8-pkp__item { display: flex; gap: 10px; align-items: baseline; font-size: var(--pos-text-md, 15px); color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-pkp__qty { color: var(--pos-tint-on, #48C76C); font-weight: 700; font-variant-numeric: tabular-nums; flex-shrink: 0; }
  .dhk8-pkp__name { flex: 1; min-width: 0; color: var(--pos-fg, #E8EFE9); }

  .dhk8-pkp__foot {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10)); padding-top: var(--pos-space-3, 12px);
  }
  .dhk8-pkp__totals { display: flex; flex-direction: column; gap: 2px; }
  .dhk8-pkp__totals strong { font-size: var(--pos-text-xl, 20px); color: var(--pos-fg, #E8EFE9); font-variant-numeric: tabular-nums; }
  .dhk8-pkp__paid { font-size: var(--pos-text-xs, 12px); color: var(--pos-tint-on, #48C76C); font-weight: 600; }
  .dhk8-pkp__due  { font-size: var(--pos-text-xs, 12px); color: var(--pos-warning, #E0A23C); font-weight: 600; }
`
